import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Search, X, Globe, Camera, Flame, ListTodo, Sparkles } from 'lucide-react';

const APP_META = {
  giraffe: { icon: Globe, label: 'Giraffe', color: '#4ECDC4' },
  framed: { icon: Camera, label: 'Framed', color: '#9B6DD4' },
  cult: { icon: Flame, label: 'Cult of One', color: '#FF6B6B' },
  uncode: { icon: ListTodo, label: 'Uncode', color: '#FFD93D' },
  zephyr: { icon: Sparkles, label: 'Zephyr', color: '#6B35B8' },
};

// Highlight matching text
function HighlightText({ text, query }) {
  if (!query || query.length < 2 || !text) return <>{text}</>;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) => 
        regex.test(part) ? (
          <mark key={i} className="bg-[#6B35B8]/40 text-[#f0f0f5] rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

export default function InlineSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(data.results || []);
      setSelectedIdx(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 250);
  };

  const goTo = (result) => {
    // Navigate to specific item, not just the app
    let path = result.path;
    
    // Add item-specific navigation
    if (result.app === 'giraffe' && result.id) {
      path = `/giraffe/${result.trip_id || result.id}`;
    } else if (result.app === 'framed' && result.id) {
      path = `/framed?photo=${result.id}`;
    } else if (result.app === 'cult' && result.id) {
      path = `/cult?mantra=${result.id}`;
    } else if (result.app === 'uncode' && result.id) {
      path = `/uncode?task=${result.id}`;
    }
    
    setQuery('');
    setResults([]);
    setIsFocused(false);
    navigate(path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setQuery('');
      setResults([]);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, results.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && results[selectedIdx]) {
      goTo(results[selectedIdx]);
    }
  };

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Scroll selected result into view
  useEffect(() => {
    if (resultsRef.current && results.length > 0) {
      const selected = resultsRef.current.querySelector(`[data-index="${selectedIdx}"]`);
      selected?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx, results.length]);

  // Group results by app
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.app]) grouped[r.app] = [];
    grouped[r.app].push(r);
  });

  const showResults = isFocused && (query.length >= 2 || loading);

  return (
    <div className="relative mb-4">
      {/* Search input */}
      <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border transition-all ${
        isFocused ? 'border-[#6B35B8] bg-white/[0.02]' : 'border-white/[0.06] hover:border-[#6b7084]'
      }`}>
        <Search size={14} className="text-[#6b7084] shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={e => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          placeholder="Search..."
          className="flex-1 bg-transparent text-[#f0f0f5] text-xs placeholder:text-[#6b7084] outline-none"
          data-testid="search-input"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults([]); }}
            className="text-[#6b7084] hover:text-[#f0f0f5] transition-colors"
          >
            <X size={12} />
          </button>
        )}
        <kbd className="text-[10px] border border-white/[0.06] rounded px-1 py-0.5 font-mono text-[#b0b4c0]">⌘K</kbd>
      </div>

      {/* Results dropdown */}
      {showResults && (
        <div
          ref={resultsRef}
          className="absolute left-0 right-0 top-full mt-2 bg-white/[0.02] border border-white/[0.06] rounded-lg shadow-xl max-h-[60vh] overflow-y-auto z-50"
          data-testid="search-results"
        >
          {loading && (
            <div className="px-4 py-4 text-center">
              <div className="w-4 h-4 rounded-full border-2 border-[#6B35B8] border-t-transparent animate-spin mx-auto" />
            </div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="px-4 py-4 text-center">
              <p className="text-xs text-[#6b7084]">No results for "{query}"</p>
            </div>
          )}

          {!loading && Object.entries(grouped).map(([app, items]) => {
            const meta = APP_META[app];
            const Icon = meta?.icon || Sparkles;
            return (
              <div key={app}>
                <div className="px-3 py-1.5 flex items-center gap-2 border-b border-white/[0.06]/50">
                  <Icon size={10} style={{ color: meta?.color }} />
                  <span className="text-[10px] tracking-wider uppercase" style={{ color: meta?.color }}>
                    {meta?.label || app}
                  </span>
                </div>
                {items.map((r) => {
                  const globalIdx = results.indexOf(r);
                  return (
                    <button
                      key={r.id}
                      data-index={globalIdx}
                      onClick={() => goTo(r)}
                      className={`w-full text-left px-3 py-2 flex items-start gap-2 transition-colors ${
                        globalIdx === selectedIdx ? 'bg-[#6B35B8]/20' : 'hover:bg-white/[0.03]'
                      }`}
                      data-testid={`search-result-${r.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#f0f0f5] truncate">
                          <HighlightText text={r.title || 'Untitled'} query={query} />
                        </p>
                        {r.subtitle && (
                          <p className="text-[10px] text-[#6b7084] truncate mt-0.5">
                            <HighlightText text={r.subtitle} query={query} />
                          </p>
                        )}
                      </div>
                      <span className="text-[9px] text-[#6b7084] shrink-0 mt-0.5 font-mono">{r.type}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
